import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ReactLenis } from "lenis/react";
import { Toaster } from "sonner";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Videos from "@/pages/Videos";
import Guests from "@/pages/Guests";
import Contact from "@/pages/Contact";
import Sponsor from "@/pages/Sponsor";
import Community from "@/pages/Community";
import News from "@/pages/News";
import NewsOriginal from "@/pages/NewsOriginal";
import NewsArticle from "@/pages/NewsArticle";
import Fairway from "@/pages/Fairway";
import Admin from "@/pages/Admin";

const SEO_MAP = {
  "/": {
    title: "Fore the Hell of It | Golf Media, Videos & Entertainment",
    description: "Fore the Hell of It is a golf entertainment and media brand — Carlos and Joe play 18 holes with interesting guests. Great guests. Questionable golf. Real conversations.",
  },
  "/about": {
    title: "About Fore the Hell of It | Golf Media Brand",
    description: "Meet Carlos Lopez and Joseph Buro — the best friends behind Fore the Hell of It, a golf media brand built on competition, conversation, and great stories.",
  },
  "/videos": {
    title: "Fore the Hell of It Videos | Golf Content & Entertainment",
    description: "Watch Fore the Hell of It videos — golf vlogs, match play, travel, challenges, and long-form conversations with interesting people on and off the course.",
  },
  "/guests": {
    title: "Fore the Hell of It Guests | Play 18 With Fore",
    description: "Athletes, founders, creators, and everyday people with stories worth eighteen holes. Apply to be a guest on Fore the Hell of It.",
  },
  "/community": {
    title: "Fore Community | Fore the Hell of It",
    description: "Golf is better with people — community rounds, charity outings, events, and partnerships. FORE creates the content; the people around it create the community.",
  },
  "/sponsor": {
    title: "Partner With Fore | Fore the Hell of It",
    description: "Partner with Fore the Hell of It. Sponsorship packages that put your brand in front of golfers, founders, and competitors who actually watch.",
  },
  "/contact": {
    title: "Contact Fore the Hell of It",
    description: "Get in touch with Fore the Hell of It — sponsorships, partnerships, guest requests, or a great course recommendation.",
  },
  "/news": {
    title: "Golf News | Fore the Hell of It Media",
    description: "The latest from around the game — live golf headlines from verified sources, linking directly to the original publisher, plus FORE Originals.",
  },
  "/partnerships": {
    title: "Fairway Partners Group | Golf Outing Partnerships by Fore the Hell of It Media",
    description: "Fairway Partners Group elevates golf outings through professional marketing, sponsorship development, sponsor activations, and memorable experiences. Services starting at $1,500.",
  },
};

const RouteSEO = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isAdmin = pathname.startsWith("/admin");
    const meta = SEO_MAP[pathname] || SEO_MAP["/"];
    const canonical = `https://forethehellofitmedia.com${pathname === "/" ? "/" : pathname}`;
    document.title = isAdmin ? "Fore the Hell of It — Internal" : meta.title;
    const setMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("name", "robots", isAdmin ? "noindex, nofollow" : "index, follow");
    if (!isAdmin) {
      setMeta("name", "description", meta.description);
      setMeta("property", "og:title", meta.title);
      setMeta("property", "og:description", meta.description);
      setMeta("property", "og:url", canonical);
      setMeta("name", "twitter:title", meta.title);
      setMeta("name", "twitter:description", meta.description);
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);
    }
  }, [pathname]);
  return null;
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname, search]);
  return null;
};

function App() {
  return (
    <div className="App min-h-screen bg-ink text-white">
      <ReactLenis root options={{ lerp: 0.09, wheelMultiplier: 1 }}>
        <BrowserRouter>
          <ScrollToTop />
          <RouteSEO />
          <div className="noise-overlay" aria-hidden="true" />
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/guests" element={<Guests />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/sponsor" element={<Sponsor />} />
            <Route path="/community" element={<Community />} />
            <Route path="/partnerships" element={<Fairway />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/original/:id" element={<NewsOriginal />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="*" element={<Home />} />
          </Routes>
          <Footer />
          <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#111", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", borderRadius: 0 } }} />
        </BrowserRouter>
      </ReactLenis>
    </div>
  );
}

export default App;
